import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Html, useGLTF, useTexture } from '@react-three/drei';
import { Card, PlayerEmoteSlug, PlayerFacingDirection, PublicGamePlayerInfo, PublicGameSession, socketKeys, Suit, SuitImages } from '../types/SharedTypes';
import { useSocket } from '../contexts/SocketContext';
import RoomPage from './Room';
import { LikeIconSolid } from '../assets/icons';
import { getPositionAroundTable, getRelativeTablePosition, Table, TableObject } from '../components/game/TableObjects';
import { MainCard, PlayingCardCanvas } from '../components/game/MainCard';
import { RoomModel, SkyBox } from '../components/game/World';
import { ModOrbit } from '../components/game/CameraControls';
import { characters, userColours } from '../assets/general';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorBoundaryFallback from '../components/shared/ErrorBoundaryFallback';
import SoundManager from '../tools/SoundManager';
import { socket } from '../socket';

type Coords = [number, number, number];

const Character = ({player, index}: {player: PublicGamePlayerInfo, index: number}) => {

  const {currentPlayer, players, game} = useSocket();
  const {invalidate} = useThree();

  const [facing, setFacing] = useState<PlayerFacingDirection>(0);
  
  const { scene } = useGLTF(`/3d/${player.character.slug}/scene.gltf`);
  const object = useMemo(() => scene.clone(true), [scene]);
  const texture = useTexture(`/3d/${player.character.slug}/textures/texture-${player.character.skin}.png`);

  const uiRef = useRef<THREE.Group>(null);
  const objRef = useRef<THREE.Mesh>(null);

  const posIndex = getRelativeTablePosition(player.position, currentPlayer?.position, players.length, index);

  const currentCharacter = characters[player.character.slug];

  const configuredTexture = useMemo(() => {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.flipY = false;
    return texture;
  }, [texture]);

  //these are separated incase the material needs to be changed again
  //since cloning is a lot more expensive
  //but the texture also doesn't change again so unnecessary
  useEffect(() => {
    object.traverse(child => {
      if (child instanceof THREE.Mesh) {
        child.material = child.material.clone();
      }
    });
  }, [object]);

  useEffect(() => {
    object.traverse(child => {
      if (child instanceof THREE.Mesh) {
        child.material.map = configuredTexture;
        //maybe need needsUpdate here
      }
    });
    invalidate();
  }, [object, configuredTexture]);

  useFrame(({ camera }) => {
    if (uiRef.current && objRef.current) {
      // Position UI relative to the target model
      uiRef.current.position.copy(objRef.current.position).add(new THREE.Vector3(0, currentCharacter.uiHeight, 0)); // Adjust height as needed

      // Make UI face the camera
      uiRef.current.quaternion.copy(camera.quaternion);
    }
  });

  useEffect(() => {

    const handlePlayerFacing = ({userId, direction}: {userId: string, direction: PlayerFacingDirection}) => {
      if (player.userId === userId) setFacing(direction);
    }

    socket.on(socketKeys.toClient.game.playerFacing, handlePlayerFacing);
    return () => {
      socket.off(socketKeys.toClient.game.playerFacing, handlePlayerFacing);
    }

  }, []);
  
  if (posIndex === 0) return null; //is current player or error

  const position = getPositionAroundTable(posIndex, players.length, 2, 2.3, facing ? facing * 0.7 : 0);

  return (
    <>
      <primitive ref={objRef} rotation={[0, position.rotation, 0]} position={position.position} scale={currentCharacter.scale} object={object} />
      {true &&
        <group ref={uiRef}>
          <mesh>
            <Html
              position={[0, 0, 0]} // Offset UI above the model
              transform
              occlude={false}
              distanceFactor={2} // Controls UI size relative to distance
              zIndexRange={[100, 0]}
            >
              <CharacterUI player={player} game={game} />
            </Html>
          </mesh>
        </group>
      }
    </>
  )
}

const CharacterUI = ({player, game}: {player: PublicGamePlayerInfo, game: PublicGameSession | null}) => {

  const [emote, setEmote] = useState<PlayerEmoteSlug | null>(null);

  const waitingTurn = game?.currentState === 'player_decision' || game?.currentState === 'dealer_decision';

  useEffect(() => {

    const handleEmote = ({userId, emote}: {userId: string, emote: PlayerEmoteSlug}) => {
      if (player.userId === userId) setEmote(emote);
    }

    socket.on(socketKeys.toClient.game.playerEmote, handleEmote)
    return () => {
      socket.off(socketKeys.toClient.game.playerEmote, handleEmote);
    }
  }, []);

  return (
    <>
      {emote &&
        <div
          className='item-row center-horizontal emote-pop full-width'
          onAnimationEnd={() => setEmote(null)}
        >
          <div className='user-avatar med allow-overflow'>
            <img src={`/img/emotes/${emote}.png`} />
          </div>
        </div>
      }
      {player.lives > 0 &&
        <div
          className='item-row center-horizontal'
          style={{
            transform: `scale(${(player.userId === game?.currentPlayer && waitingTurn) ? 1 : 0.55})`,
            transition: "transform 0.3s ease",
            filter: `grayscale(${(player.userId === game?.currentPlayer && waitingTurn) ? 0 : 0.5})`
          }}
        >
          <div
            className={`item-list center-horizontal ${(player.userId === game?.currentPlayer && waitingTurn) ? 'pulse-animation' : ''}`}
          >
            <div style={{background: userColours[player.colour], padding: "4px", borderRadius: "24px"}}>
              <div className='item-row' style={{background: "white", padding: "8px", borderRadius: "20px"}}>
                <p className='body always-black' style={{ margin: 0 }}>{player.name}</p>
                {player.lives !== -1 && 
                  <div className='item-row small-gap'>
                    <p className='subheading-large weight-bold always-black' style={{ margin: 0 }}>{player.lives}</p>
                    <div className='icon-display xsmall red'>
                      <LikeIconSolid />
                    </div>
                    {/* <p style={{ margin: 0 }}>pos: {player.position}</p> */}
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      }
      {player.lives < 1 &&
        <p className='heading-large'>☠️</p>
      }
    </>
  )
}

const GameCanvas = ({cardCanvasRef, currentCanvasCard}: {
  cardCanvasRef: React.RefObject<HTMLCanvasElement | null>,
  currentCanvasCard: Card | null
}) => {

  const {players, game, currentDealer} = useSocket();
  const {invalidate} = useThree();
  const [dummyTargets] = useState(() => [new THREE.Object3D(), new THREE.Object3D(), new THREE.Object3D()]);

  useEffect(() => {
    console.log("inval playerslength");
    
    invalidate();
  }, [players.length]);

  useEffect(() => {
    SoundManager.playAmbient();
    return () => SoundManager.stopAmbient();
  }, []);

  return (
    <>
      <color attach="background" args={['#a39eae']} />
      <MainCard canvasRef={cardCanvasRef} currentCanvasCard={currentCanvasCard} />
      {players.map((player, i) => 
        <Character key={player.userId} player={player} index={i} />
      )}
      {(game && currentDealer) ? <Table /> : <TableObject />}
      <RoomModel />
      <SkyBox />
      {/* <mesh receiveShadow rotation={[Math.PI / -2, Math.PI / -1, 0]} position={[0,6.2,0]}>
        <planeGeometry args={[29, 14]} />
        <meshStandardMaterial color={'white'} />
      </mesh> */}
      {/* <ambientLight intensity={1} color={"white"} /> */}
      <directionalLight target={dummyTargets[0]} intensity={5} position={[1, 12, 5]} color="white" />
      <primitive object={dummyTargets[0]} position={[-2, 7, 3]} />
      <directionalLight target={dummyTargets[1]} intensity={2} position={[1, 12, 5]} color="white" />
      <primitive object={dummyTargets[1]} position={[60, -12, -20]} />
      <directionalLight target={dummyTargets[2]} intensity={0.5} position={[1, 12, 5]} color="white" />
      <primitive object={dummyTargets[2]} position={[-9, 12, 0]} />
      {/* <gridHelper /> */}
      <ModOrbit />
      <AudioSetup />
    </>
  )
}

const GameRoomPage = () => {

  const {currentCard, setSuitImages, game} = useSocket();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentCanvasCard, setCurrentCanvasCard] = useState<Card | null>(null);
  const screenWidth = window.innerWidth;

  const preloadSuits = () => {
    const suits: Record<Suit, string> = {
      h: "/art/h_suit.png",
      d: "/art/d_suit.png",
      c: "/art/c_suit.png",
      s: "/art/s_suit.png",
    };

    const promises = Object.entries(suits).map(
      ([name, src]) =>
        new Promise<[Suit, HTMLImageElement]>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve([name as Suit, img]);
          img.onerror = reject;
          img.src = src;
        })
    );

    Promise.all(promises).then((loaded) => {
      const imgs = Object.fromEntries(loaded) as SuitImages;
      setSuitImages(imgs);
    });
  }

  useEffect(() => {
    preloadSuits();
  }, []);

  function Loader() {
    return <Html center><p className='heading-large'>Loading…</p></Html>;
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
      <div>
        {/* can use for swipe options */}
        {/* <CardGestureArea /> */}
        <div className='ui full-width'>
          <PlayingCardCanvas card={currentCard} canvasRef={canvasRef} setCurrentCanvasCard={setCurrentCanvasCard} />
          <RoomPage />
        </div>
        <Canvas
          camera={{fov: screenWidth > 500 ? 50 : 65}}
          frameloop='demand'
          dpr={1}
        >
          <Suspense fallback={<Loader />}>
            <GameCanvas cardCanvasRef={canvasRef} currentCanvasCard={currentCanvasCard} />
          </Suspense>
        </Canvas>
      </div>
    </ErrorBoundary>
  )
}

const AudioSetup = () => {
  const { camera } = useThree();

  useEffect(() => {
    const listener = new THREE.AudioListener();
    camera.add(listener);
    SoundManager.init(listener);
  }, [camera]);

  return null;
};

export default GameRoomPage;